import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Instagram } from "lucide-react";
import { FALLBACK_TICKET_URL } from "../../lib/api";
import { useLang, T } from "../../lib/i18n";

const links = [
  { to: "/", key: "home" },
  { href: "https://eventcartel.com/events/festival/new-year-gala-2027-st-martin-PR-kalos-beach-lounge-restaurant-tickets-15751?promo=58786cd1f7dcef4d424a317f8c8bfd1c", key: "gala" },
  { href: "https://eventcartel.com/events/festival/new-year-after-party-first-sunrise-2027-st-martin-PR-villa-aura-tickets-15753?promo=688135d9ad39a9bd3f542c2c0a0fecfd", key: "afterparty" },
  { href: "https://eventcartel.com/events/festival/first-sunset-of-2027-st-martin-PR-villa-aura-tickets-15755?promo=17dd1c6942ba9d2b4c1e63bbd88995ca", key: "sunset" },
];

export const Nav = ({ ticketUrl }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { lang, toggle } = useLang();
  const t = T[lang].nav;
  const url = ticketUrl || FALLBACK_TICKET_URL;
  const onHero = pathname !== "/" ? false : !scrolled;

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        onHero && !open ? "bg-transparent" : "bg-[#FDFBF7]/75 backdrop-blur-xl border-b border-slate-900/5"
      }`}
    >
      <div className="max-w-[1440px] mx-auto flex items-center justify-between px-5 md:px-10 h-16 md:h-20">
        <Link to="/" data-testid="nav-logo" className={`font-serif text-lg md:text-xl tracking-tight ${onHero && !open ? "text-white" : "text-slate-900"}`}>
          Vse&nbsp;Nashi&nbsp;<span className="italic font-medium">SXM</span>
          <span className="text-[#00B4D8]">.</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) =>
            l.href ? (
              <a
                key={l.key}
                href={l.href}
                target="_blank"
                rel="noopener"
                data-testid={`nav-link-${l.key}`}
                className={`text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                  onHero ? "text-white/80 hover:text-white" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {t[l.key]}
              </a>
            ) : (
              <Link
                key={l.key}
                to={l.to}
                data-testid={`nav-link-${l.key}`}
                className={`text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                  onHero ? "text-white/80 hover:text-white" : "text-slate-500 hover:text-slate-900"
                } ${pathname === l.to ? (onHero ? "text-white" : "text-slate-900") : ""}`}
              >
                {t[l.key]}
              </Link>
            )
          )}
          <a
            href="https://www.instagram.com/vsenashinyc?igsi=ZTRianU0dXZkYzJw"
            target="_blank"
            rel="noopener"
            aria-label="Instagram"
            data-testid="nav-instagram"
            className={`p-2 rounded-full transition-colors duration-300 ${
              onHero ? "text-white/80 hover:text-white" : "text-slate-500 hover:text-slate-900"
            }`}
          >
            <Instagram size={18} />
          </a>
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className={`text-xs uppercase tracking-[0.2em] rounded-full border px-4 py-2 transition-colors duration-300 ${
              onHero ? "text-white border-white/40 hover:bg-white/10" : "text-slate-700 border-slate-900/20 hover:border-slate-900"
            }`}
          >
            {lang === "en" ? "RU" : "EN"}
          </button>
          <a
            href={url}
            target="_blank"
            rel="noopener"
            data-testid="nav-buy-tickets"
            className="group inline-flex items-center gap-2 rounded-full bg-[#00B4D8] hover:bg-[#0096b4] text-white text-xs uppercase tracking-[0.2em] px-6 py-3 transition-colors duration-300"
          >
            {t.buy}
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </nav>

        <div className="md:hidden flex items-center gap-2">
          <a
            href="https://www.instagram.com/vsenashinyc?igsi=ZTRianU0dXZkYzJw"
            target="_blank"
            rel="noopener"
            aria-label="Instagram"
            data-testid="nav-instagram-mobile"
            className={`p-2 ${onHero && !open ? "text-white" : "text-slate-900"}`}
          >
            <Instagram size={20} />
          </a>
          <button
            data-testid="lang-toggle-mobile"
            onClick={toggle}
            className={`text-xs uppercase tracking-[0.2em] rounded-full border px-3.5 py-1.5 ${onHero && !open ? "text-white border-white/40" : "text-slate-700 border-slate-900/20"}`}
          >
            {lang === "en" ? "RU" : "EN"}
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className={`p-2 ${onHero && !open ? "text-white" : "text-slate-900"}`}
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#FDFBF7]/95 backdrop-blur-xl border-b border-slate-900/5"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l, i) =>
                l.href ? (
                  <a
                    key={l.key}
                    href={l.href}
                    target="_blank"
                    rel="noopener"
                    data-testid={`nav-mobile-link-${l.key}`}
                    className="font-serif text-3xl text-slate-900"
                    style={{ transitionDelay: `${i * 40}ms` }}
                  >
                    {t[l.key]}
                  </a>
                ) : (
                  <Link
                    key={l.key}
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.key}`}
                    className="font-serif text-3xl text-slate-900"
                    style={{ transitionDelay: `${i * 40}ms` }}
                  >
                    {t[l.key]}
                  </Link>
                )
              )}
              <a
                href={url}
                target="_blank"
                rel="noopener"
                data-testid="nav-mobile-buy-tickets"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#00B4D8] text-white text-sm uppercase tracking-[0.2em] px-8 py-4"
              >
                {t.buy} <ArrowUpRight size={16} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
